import { tokenize } from './tokenize';
import { charInfo, fold, graphemes, nfc } from './text';

/**
 * Deterministic monotonic-orthography rule: every word of two or more syllables carries exactly one
 * τόνος. Missing accents are the most penalised slip in written exams, so this check never depends
 * on the language model.
 *
 * Syllables are counted from vowel groups; the digraphs αι ει οι υι ου αυ ευ ηυ count as one vowel
 * unless the second letter carries a dialytika (κοροϊδεύω) or the first carries the accent
 * (e.g. "άι"). Words pronounced with synizesis (για, μια, πιο, ποιος…) are monosyllabic and take no
 * accent. Words written entirely in capitals are skipped (capitals drop the accent).
 */
const VOWELS = new Set(['α', 'ε', 'η', 'ι', 'ο', 'υ', 'ω']);
const DIGRAPHS = new Set(['αι', 'ει', 'οι', 'υι', 'ου', 'αυ', 'ευ', 'ηυ']);

/** Monosyllables by synizesis + other unaccented words that the vowel counter would see as 2+. */
const NO_ACCENT = new Set(
  [
    'για', 'γεια', 'μια', 'μιας', 'πια', 'πιο', 'δυο', 'ποιος', 'ποια', 'ποιο', 'ποιοι', 'ποιες', 'ποιους', 'ποιον', 'ποιων',
    'ποιανού', 'μιαν', 'γιος', 'γιοι', 'γιου', 'γιο', 'γιους', 'βιος', 'πιω', 'πιεις', 'δειος', 'ζιο', 'νιος', 'νια', 'νιο', 'καινούριος',
  ].map((w) => fold(w)),
);

export function syllableCount(word: string): number {
  const gs = graphemes(nfc(word)).map((g) => charInfo(g.text));
  let count = 0;
  let i = 0;
  while (i < gs.length) {
    const c = gs[i]!;
    if (!VOWELS.has(c.base)) {
      i++;
      continue;
    }
    const next = gs[i + 1];
    if (next && VOWELS.has(next.base) && DIGRAPHS.has(c.base + next.base) && !next.hasDiaeresis && !c.hasTonos) {
      count++;
      i += 2;
      continue;
    }
    count++;
    i++;
  }
  return count;
}

export interface MissingTonos {
  start: number;
  end: number;
  word: string;
}

export function hasTonos(word: string): boolean {
  return graphemes(nfc(word)).some((g) => charInfo(g.text).hasTonos);
}

/** All polysyllabic words in `text` written without any τόνος. Offsets refer to the NFC text. */
export function findMissingTonos(text: string): MissingTonos[] {
  const src = nfc(text);
  const out: MissingTonos[] = [];
  for (const t of tokenize(src)) {
    if (t.kind !== 'word') continue;
    const w = t.text.replace(/[’'ʼ‘]$/u, '');
    if (!/\p{Script=Greek}/u.test(w)) continue;
    if (w === w.toLocaleUpperCase('el') && w.length > 1) continue; // ALL CAPS
    if (hasTonos(w)) continue;
    if (NO_ACCENT.has(fold(w))) continue;
    if (syllableCount(w) < 2) continue;
    out.push({ start: t.start, end: t.end, word: src.slice(t.start, t.end) });
  }
  return out;
}
